/* eslint-disable */
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ImgPoint from '../../../../../../assets/2025/arrow.gif';
import './index.less';

export interface IIntegratedServiceItem {
  id?: string | number;
  label?: string;
  content?: React.ReactNode;
}

export interface IIntegratedServiceListProps {
  title?: string;
  description?: string;
  image?: string;
  items?: IIntegratedServiceItem[];
  id?: string;
  onComplete?: () => void;
}

const IntegratedServiceList: React.FunctionComponent<
  IIntegratedServiceListProps
> = ({ title, description, image, items = [], id, onComplete }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [viewedIndices, setViewedIndices] = useState<Set<number>>(new Set([0]));
  const hasCompletedRef = useRef(false);

  useEffect(() => {
    setViewedIndices(new Set([0]));
    setActiveIndex(0);
    hasCompletedRef.current = false;
  }, [items.length]);

  useEffect(() => {
    if (
      items.length > 0 &&
      viewedIndices.size >= items.length &&
      !hasCompletedRef.current
    ) {
      hasCompletedRef.current = true;
      onComplete?.();
    }
  }, [viewedIndices, items, onComplete]);

  const handleClick = useCallback((index: number) => {
    const allViewed = viewedIndices.size >= items.length;
    const maxViewed = Math.max(...Array.from(viewedIndices));
    if (!allViewed && index !== maxViewed + 1 && !viewedIndices.has(index)) return;
    setActiveIndex(index);
    setViewedIndices((prev) => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, [viewedIndices, items.length]);

  const guideIndex = useMemo(() => {
    const unviewed = items.map((_, i) => i).filter((i) => !viewedIndices.has(i));
    return unviewed.length ? Math.min(...unviewed) : null;
  }, [items, viewedIndices]);

  if (!items.length) return null;

  const currentItem = items[activeIndex];

  return (
    <div className="integrated-service-list" id={id}>
      <div className="integrated-service-list-container">
        {image && (
          <div className="integrated-service-list-image">
            <img src={image} alt="" />
          </div>
        )}
        <div className="integrated-service-list-content">
          {title && (
            <div
              className="integrated-service-list-title"
              dangerouslySetInnerHTML={{
                __html: title || '',
              }}
            ></div>
          )}
          {description && (
            <p className="integrated-service-list-desc">{description}</p>
          )}
          <div className="integrated-service-list-body">
            <div className="integrated-service-list-items">
              {items.map((item, index) => {
                const isActive = index === activeIndex;
                return (
                  <div
                    key={item.id ?? index}
                    className={`integrated-service-list-item${
                      isActive ? ' is-active' : ''
                    }`}
                    onClick={() => handleClick(index)}
                  >
                    {guideIndex === index && (
                      <img
                        src={ImgPoint}
                        alt=""
                        className="integrated-service-list-item-guide"
                      />
                    )}
                    <span className="integrated-service-list-item-label">
                      {item.label}
                    </span>
                    <span className="integrated-service-list-item-arrow">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 12L10 8L6 4"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="integrated-service-list-detail">
              {currentItem?.content}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(IntegratedServiceList);
